import {
  Loading,
  ShowContextProvider,
  useShowController,
  useLocaleState,
  useTranslate,
} from 'react-admin'
import { PromotionAuthor } from '../../components/domains/promotion/PromotionAuthor'
import { PromotionTitle } from '../../components/domains/promotion/PromotionTitle'
import { PromotionImgHeader } from '../../components/domains/promotion/PromotionImgHeader'
import { PromotionContent } from '../../components/domains/promotion/PromotionContent'
import {
  Box,
  Card,
  Container,
  Stack,
  styled,
  Grid,
  Typography,
  TypographyProps,
  Paper,
} from '@mui/material'
import ModifyTimeStamp from '../../components/ModifyTimeStamp'
import { ListActions } from '../../components/list-actions'
import PromotionImages from '../../components/domains/promotion/PromotionImages'
import { PROMOTIONS_FIELDS } from '../../providers/localization/constant'
import { ImageField } from '../../components/fields'

const StackStyled = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(4, 5),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4, 10),
  },
}))

const PreviewThumbnail = styled(ImageField)(() => ({
  '& .RaImageField-image': {
    width: '100%',
    height: 300,
    objectFit: 'cover',
    objectPosition: '50% 50%',
    borderRadius: 5,
  },
}))

const PaperStyled = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  textAlign: 'center',
  color: theme.palette.text.secondary,
  height: 300,
  width: '100%',
  lineHeight: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .RaImageField-image': {
    verticalAlign: 'middle',
  },
}))

const ContentWrapper = styled(Box)(() => ({ display: 'block', mb: 3 }))

const Heading: React.FC<TypographyProps> = ({ children, ...props }) => {
  return (
    <Typography variant='subtitle2' sx={{ color: '#ADB3BD', fontWeight: 'normal' }} {...props}>
      {children}
    </Typography>
  )
}

export const PromotionShow: React.FC = () => {
  const [locale] = useLocaleState()
  const langActive = locale
  const showController = useShowController()
  const translate = useTranslate()
  const { isLoading, record } = showController
  if (isLoading) return <Loading />

  return (
    <ShowContextProvider value={showController}>
      <Container maxWidth='md'>
        <ListActions
          hasCreate={false}
          hasExport={false}
          hasList={true}
          hasPreview={true}
          hasEdit={true}
        />
        <Card>
          <PromotionImgHeader source='thumbnail.full_path' />
          <StackStyled spacing={1}>
            <ModifyTimeStamp />
            <PromotionTitle source={`title_${langActive}`} />
            <Stack direction='row' alignItems='center' spacing={0.5}>
              <PromotionAuthor authorSource='create_by' dateSource='create_at' /> 
            </Stack>
              <ContentWrapper>
                <Heading>
                  {translate(`${PROMOTIONS_FIELDS}.description`)}
                </Heading>
                <PromotionContent source={`description_${langActive}`} />
              </ContentWrapper>
              <br />
              <ContentWrapper>
                <Heading>
                  {translate(`${PROMOTIONS_FIELDS}.short_description`)}
                </Heading>
                <PromotionContent source={`short_description_${langActive}`} />
              </ContentWrapper>
              <Grid item xs={12} md={6} sx={{ pt: 3, mb: 3 }} order={{ xs: '0', md: '1' }}>
                <ContentWrapper>
                  <Heading>
                    {translate(`${PROMOTIONS_FIELDS}.thumbnail.label`)}
                  </Heading>
                  {record.thumbnail ? (
                    <PreviewThumbnail source='thumbnail.full_path' />
                  ) : (
                    <PaperStyled variant='outlined'>
                      <Typography variant='caption' sx={{ color: 'error.main' }}>
                        {translate(`${PROMOTIONS_FIELDS}.thumbnail.notfound`)}
                      </Typography>
                    </PaperStyled>
                  )}
                </ContentWrapper>
              </Grid>
          </StackStyled>
        </Card>
        <br />
        <Card>
          <StackStyled spacing={1}>
            {/* show images not found layout when images is an empty array */}
            {(record.images && record.images.length > 0) ? (
              <PromotionImages source='images' title='name' />
            ) : (
              <PaperStyled variant='outlined'>
                <Typography variant='caption' sx={{ color: 'error.main' }}>
                  {translate(`${PROMOTIONS_FIELDS}.images.notfound`)}
                </Typography>
              </PaperStyled>
            )}
          </StackStyled>
        </Card>
      </Container>
    </ShowContextProvider>
  )
}

export default PromotionShow
